import { CONTENT_BLOCK_TYPES } from "../constants/contentBlockConstants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// 扩展dayjs插件以支持UTC处理
dayjs.extend(utc);

/**
 * UTC时区感知的日期格式化函数
 * 确保航班日期在内容块中统一使用UTC时区，避免本地时区影响
 * @param {string} dateString - UTC日期字符串
 * @param {string} format - 格式化模式，默认为 YYYY-MM-DD
 * @returns {string} 格式化后的日期字符串
 */
const formatDateUTC = (dateString, format = "YYYY-MM-DD") => {
    if (!dateString) return "";

    try {
        // 强制使用UTC解析，避免本地时区影响
        return dayjs.utc(dateString).format(format);
    } catch (error) {
        console.error("UTC日期格式化错误:", error, { dateString, format });
        return "";
    }
};

/**
 * UTC时区感知的时间格式化函数
 * 确保航班时间在内容块中统一使用UTC时区，避免本地时区影响
 * @param {string} timeString - UTC时间字符串
 * @param {string} format - 格式化模式，默认为 HH:mm
 * @returns {string} 格式化后的时间字符串
 */
const formatTimeUTC = (timeString, format = "HH:mm") => {
    if (!timeString) return "";

    try {
        // 强制使用UTC解析，避免本地时区影响
        return dayjs.utc(timeString).format(format);
    } catch (error) {
        console.error("UTC时间格式化错误:", error, { timeString, format });
        return "";
    }
};

/**
 * 生成航班信息内容
 */
export const generateFlightContent = (flightData) => {
    if (!flightData) return "<p><i>请从右侧选择航班信息</i></p>";
    return `
        <p><strong>航班号:</strong> ${flightData.flight_number || "N/A"}</p>
        <p><strong>航线:</strong> ${flightData.departure_airport || "N/A"} -> ${flightData.arrival_airport || "N/A"}</p>
        <p><strong>起飞/到达:</strong> ${flightData.std ? formatTimeUTC(flightData.std) : "N/A"} / ${flightData.sta ? formatTimeUTC(flightData.sta) : "N/A"}</p>
    `;
};

/**
 * 生成附件内容
 */
export const generateAttachmentContent = (attachmentData) => {
    if (!attachmentData) return "<p><i>请从右侧选择附件</i></p>";
    return `
        <p><strong>附件:</strong> <a href="${attachmentData.url || "#"}" target="_blank">${attachmentData.filename || "未命名文件"}</a> (${attachmentData.size || "N/A"})</p>
    `;
};

/**
 * 生成联系人内容
 */
export const generateContactContent = (contactData) => {
    if (!contactData) return "<p><i>请从右侧选择联系人</i></p>";
    return `
        <p><strong>联系人:</strong> ${contactData.name || "N/A"}</p>
        <p><strong>职位:</strong> ${contactData.position || "N/A"}</p>
        <p><strong>电话:</strong> ${contactData.phone || "N/A"}</p>
    `;
};

/**
 * 生成航班计划列表内容
 * 注意：内容块中所有时间日期统一使用UTC时区，避免本地时区影响导致日期差异
 */
export const generateScheduleFlightListContent = (flightData) => {
    if (!flightData || !Array.isArray(flightData) || flightData.length === 0) {
        return "<p>暂无航班计划数据</p>";
    }

    const rows = flightData
        .map(
            (flight) => `
        <tr>
            <td>${flight.flight_number || ""}</td>
            <td>${formatDateUTC(flight.flight_date)}</td>
            <td>${flight.departure_airport || ""} -> ${flight.arrival_airport || ""}</td>
            <td>${formatTimeUTC(flight.std)}</td>
            <td>${formatTimeUTC(flight.sta)}</td>
            <td>${flight.aircraft_reg || ""}</td>
        </tr>
    `,
        )
        .join("");

    return `
        <table style="width:100%; border-collapse: collapse;">
            <thead>
                <tr style="background-color:#f2f2f2;">
                    <th style="padding: 8px; border: 1px solid #ddd;">航班号</th>
                    <th style="padding: 8px; border: 1px solid #ddd;">日期</th>
                    <th style="padding: 8px; border: 1px solid #ddd;">航线</th>
                    <th style="padding: 8px; border: 1px solid #ddd;">起飞</th>
                    <th style="padding: 8px; border: 1px solid #ddd;">到达</th>
                    <th style="padding: 8px; border: 1px solid #ddd;">机号</th>
                </tr>
            </thead>
            <tbody>
                ${rows}
            </tbody>
        </table>
    `;
};

/**
 * 生成航班计划基本信息内容
 */
export const generateScheduleInfoContent = (scheduleData) => {
    if (!scheduleData) {
        return "<p>暂无计划基本信息</p>";
    }

    const getStatusText = (status) => {
        const map = {
            DRAFT: "草稿",
            SUBMITTED: "已提交",
            CONFIRMATION_PENDING: "待确认",
            AIRLINE_CONFIRMED: "航司确认",
            PUBLISHED: "已发布",
        };
        return map[status] || status;
    };

    return `
        <p><strong>计划名称:</strong> ${scheduleData.name || "N/A"}</p>
        <p><strong>计划周期:</strong> ${formatDateUTC(scheduleData.start_date)} - ${formatDateUTC(scheduleData.end_date)}</p>
        <p><strong>状态:</strong> ${getStatusText(scheduleData.status)}</p>
        <p><strong>航班数:</strong> ${scheduleData.flight_count || "N/A"}</p>
    `;
};

/**
 * 生成计划航班列表（含对比）内容
 * 注意：内容块中所有时间日期统一使用UTC时区，避免本地时区影响导致日期差异
 */
export const generateScheduleFlightListWithComparisonContent = (comparisonData) => {
    console.log("🔍 [generateScheduleFlightListWithComparisonContent] 输入数据:", comparisonData);

    if (!comparisonData || !comparisonData.flights) {
        console.log("🔍 [generateScheduleFlightListWithComparisonContent] 无数据或无flights字段");
        return "<p>暂无对比数据</p>";
    }

    const { flights = [], summary = {} } = comparisonData;

    console.log("🔍 [generateScheduleFlightListWithComparisonContent] 处理数据:", {
        flightsCount: flights.length,
        summary,
        flightsSample: flights.slice(0, 3).map((f) => ({
            flight_number: f.flight_number,
            status: f._comparisonStatus,
            std: f.std,
            flight_date: f.flight_date,
            aircraft_reg: f.aircraft_reg,
            departure_airport: f.departure_airport,
            arrival_airport: f.arrival_airport,
        })),
    });

    if (flights.length === 0) {
        console.log("🔍 [generateScheduleFlightListWithComparisonContent] 航班数组为空");
        return "<p>暂无航班数据</p>";
    }

    // 生成状态标签的样式
    const getStatusBadge = (status) => {
        const statusConfig = {
            ADDED: { text: "新增", color: "#52c41a", bgColor: "#f6ffed" },
            CHANGED: { text: "变更", color: "#fa8c16", bgColor: "#fff7e6" },
            REMOVED: { text: "删除", color: "#ff4d4f", bgColor: "#fff2f0" },
        };

        const config = statusConfig[status];
        if (!config) return ""; // 无变化状态不显示标签

        return `<span style="
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
            color: ${config.color};
            background-color: ${config.bgColor};
            border: 1px solid ${config.color};
        ">${config.text}</span>`;
    };

    // 辅助函数：获取星期几（UTC时区感知）
    const getDayOfWeek = (dateStr) => {
        if (!dateStr) return "";
        try {
            // 使用UTC解析避免本地时区影响
            const date = dayjs.utc(dateStr);
            const days = ["7", "1", "2", "3", "4", "5", "6"]; // 周日=7, 周一=1, ..., 周六=6
            return days[date.day()];
        } catch (error) {
            console.error("获取星期几错误:", error, { dateStr });
            return "";
        }
    };

    // 辅助函数：格式化日期为DD-MMM格式（UTC时区感知）
    const formatDateForTable = (dateStr) => {
        if (!dateStr) return "";
        try {
            // 使用UTC解析避免本地时区影响
            const date = dayjs.utc(dateStr);
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            return `${date.date().toString().padStart(2, "0")}-${months[date.month()]}`;
        } catch (error) {
            console.error("格式化日期错误:", error, { dateStr });
            return "";
        }
    };

    // 辅助函数：格式化时间为HH:MM格式（UTC时区感知）
    const formatTimeForTable = (timeStr) => {
        if (!timeStr) return "";
        try {
            // 使用UTC解析避免本地时区影响
            return dayjs.utc(timeStr).format("HH:mm");
        } catch (error) {
            console.error("格式化时间错误:", error, { timeStr });
            return "";
        }
    };

    // 生成航班行
    const rows = flights
        .map((flight) => {
            const isRemoved = flight._comparisonStatus === "REMOVED";
            const rowStyle = isRemoved ? 'style="text-decoration: line-through; opacity: 0.6; background-color: #fff2f0;"' : "";

            // 状态标签放在最后一列
            const statusCell = flight._comparisonStatus ? getStatusBadge(flight._comparisonStatus) : "";

            return `
                <tr ${rowStyle}>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px;">${formatDateForTable(flight.flight_date)}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px;">${flight.aircraft_reg || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${getDayOfWeek(flight.flight_date)}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; font-weight: bold;">${flight.flight_number || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${flight.departure_airport || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${flight.arrival_airport || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${formatTimeForTable(flight.std)}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${formatTimeForTable(flight.sta)}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px;">${flight.stc_type || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${flight.block_hours || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${flight.ground_time || ""}</td>
                    <td style="padding: 4px 6px; border: 1px solid #ddd; font-size: 11px; text-align: center;">${statusCell}</td>
                </tr>
            `;
        })
        .join("");

    // 生成汇总信息
    const summaryHtml =
        summary && Object.keys(summary).length > 0
            ? `
        <div style="margin-bottom: 16px; padding: 12px; background-color: #f0f8ff; border: 1px solid #d4edda; border-radius: 4px;">
            <h4 style="margin: 0 0 8px 0; color: #0c5460;">变更汇总</h4>
            <p style="margin: 0; color: #0c5460;">
                总航班: ${summary.totalFlights || 0} 班 | 
                <span style="color: #52c41a;">新增: ${summary.addedCount || 0}</span> | 
                <span style="color: #fa8c16;">变更: ${summary.changedCount || 0}</span> | 
                <span style="color: #ff4d4f;">删除: ${summary.removedCount || 0}</span>
                ${summary.unchangedCount > 0 ? ` | <span style="color: #8c8c8c;">无变化: ${summary.unchangedCount}</span>` : ""}
            </p>
        </div>
    `
            : "";

    return `
        ${summaryHtml}
        <table style="width:100%; border-collapse: collapse; font-size: 11px;">
            <thead>
                <tr style="background-color:#f2f2f2;">
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">Date</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">A/C REG</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">Day</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">Flight</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">DEP</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">ARR</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">STD</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">STA</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">STC</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">BH</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">GT</th>
                    <th style="padding: 6px; border: 1px solid #ddd; text-align: center; font-size: 11px; font-weight: bold;">Status</th>
                </tr>
            </thead>
            <tbody>
                ${rows}
            </tbody>
        </table>
    `;
};

/**
 * 生成通用内容
 */
export const generateGenericContent = (data) => {
    if (typeof data === "object" && data !== null) {
        return `<pre>${JSON.stringify(data, null, 2)}</pre>`;
    }
    return `<p>${String(data)}</p>`;
};

/**
 * 主要的内容生成分发函数
 */
export const generateContentForContentBlock = (contentBlockType, data) => {
    switch (contentBlockType) {
        case CONTENT_BLOCK_TYPES.FLIGHT_INFO:
            return generateFlightContent(data);
        case CONTENT_BLOCK_TYPES.ATTACHMENT:
            return generateAttachmentContent(data);
        case CONTENT_BLOCK_TYPES.CONTACT:
            return generateContactContent(data);
        case CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST:
            return generateScheduleFlightListContent(data);
        case CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST_WITH_COMPARISON:
            return generateScheduleFlightListWithComparisonContent(data);
        case CONTENT_BLOCK_TYPES.SCHEDULE_INFO:
            return generateScheduleInfoContent(data);
        default:
            return generateGenericContent(data);
    }
};
